/**
 * Home-screen install + storage durability.
 *
 * Why these two live together: on iOS they are the same problem. Safari
 * deletes a site's IndexedDB after 7 days without a visit, and a
 * home-screen web app is exempt from that rule because it keeps its own
 * usage counter. So on iPhone, installing IS the durability measure.
 *
 * The iOS trap this module exists to avoid: a home-screen app gets its
 * OWN IndexedDB, isolated from Safari's. Anything logged in the browser
 * does not appear in the installed app. That is why we prompt to install
 * early, before a family has a log worth stranding.
 */
import { useCallback, useEffect, useState } from 'react';

/** Chrome/Edge fire this so we can show a real install prompt later. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type Platform = 'ios' | 'android' | 'desktop';

export function detectPlatform(): Platform {
  const ua = navigator.userAgent;
  // iPadOS 13+ reports as Macintosh; the touch check separates it from a Mac.
  const isIos =
    /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  if (isIos) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'desktop';
}

/** True when running as an installed app rather than a browser tab. */
export function isInstalled(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS Safari predates display-mode and still uses this.
    ('standalone' in navigator && (navigator as { standalone?: boolean }).standalone === true)
  );
}

const DISMISS_KEY = 'installPromptDismissedAt';
/** Re-offer the prompt this long after a dismissal. */
const DISMISS_DAYS = 10;

function dismissedRecently(): boolean {
  const raw = localStorage.getItem(DISMISS_KEY);
  if (!raw) return false;
  const at = Number(raw);
  if (!Number.isFinite(at)) return false;
  return Date.now() - at < DISMISS_DAYS * 86_400_000;
}

export interface InstallState {
  /** Show install UI at all? */
  shouldOffer: boolean;
  platform: Platform;
  /** Android/Chrome only: a real prompt is available. */
  canPromptNatively: boolean;
  promptInstall: () => Promise<'accepted' | 'dismissed' | 'unavailable'>;
  dismiss: () => void;
}

export function useInstall(): InstallState {
  const [platform] = useState(detectPlatform);
  const [installed, setInstalled] = useState(isInstalled);
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(dismissedRecently);

  useEffect(() => {
    const onBeforeInstall = (e: Event) => {
      // Keep the event so the prompt can be shown on our own terms, from a
      // user gesture, rather than whenever the browser feels like it.
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    const mq = window.matchMedia('(display-mode: standalone)');
    const onDisplayChange = () => setInstalled(isInstalled());
    mq.addEventListener('change', onDisplayChange);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
      mq.removeEventListener('change', onDisplayChange);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferred) return 'unavailable' as const;
    await deferred.prompt();
    const { outcome } = await deferred.userChoice;
    setDeferred(null);
    return outcome;
  }, [deferred]);

  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  }, []);

  // Desktop browsers without a native prompt have nothing useful to offer.
  const offerable = platform !== 'desktop' || deferred !== null;

  return {
    shouldOffer: !installed && !dismissed && offerable,
    platform,
    canPromptNatively: deferred !== null,
    promptInstall,
    dismiss,
  };
}

// ── Storage durability ────────────────────────────────────────────────────

export interface StorageStatus {
  /** Browser has marked this origin's storage as persistent. */
  persisted: boolean;
  /** Whether the browser exposes the Storage API at all. */
  supported: boolean;
  usageBytes?: number;
  quotaBytes?: number;
}

/**
 * Whether asking would put a browser dialog in front of the user.
 *
 * Firefox shows a modal permission prompt for persistent storage, so we
 * must only ask from a deliberate tap. 'denied' means they already said no
 * and asking again would just nag.
 */
export async function persistPermissionState(): Promise<
  'granted' | 'prompt' | 'denied' | 'unknown'
> {
  if (!navigator.permissions?.query) return 'unknown';
  try {
    const status = await navigator.permissions.query({
      name: 'persistent-storage' as PermissionName,
    });
    return status.state;
  } catch {
    // Browsers that do not know the name throw; treat as "might prompt".
    return 'unknown';
  }
}

/**
 * Ask the browser to protect our storage from automatic eviction.
 *
 * Chrome/Edge decide silently from engagement signals (installed as an app
 * counts for a lot). Firefox shows a permission dialog. Safari does not
 * implement a meaningful grant, which is exactly why installing matters
 * more there.
 *
 * Only ever call this from a user gesture, after explaining what it does:
 * see the note in main.tsx.
 *
 * This never protects against a user deliberately clearing site data; no
 * web API can. It protects against the browser reclaiming space on its own.
 */
export async function requestPersistence(): Promise<StorageStatus> {
  if (!navigator.storage?.persist) return { persisted: false, supported: false };
  try {
    const already = (await navigator.storage.persisted?.()) ?? false;
    const persisted = already || (await navigator.storage.persist());
    return { ...(await storageEstimate()), persisted, supported: true };
  } catch {
    return { persisted: false, supported: false };
  }
}

export async function storageStatus(): Promise<StorageStatus> {
  if (!navigator.storage?.persisted) return { persisted: false, supported: false };
  try {
    return {
      ...(await storageEstimate()),
      persisted: await navigator.storage.persisted(),
      supported: true,
    };
  } catch {
    return { persisted: false, supported: false };
  }
}

async function storageEstimate(): Promise<{ usageBytes?: number; quotaBytes?: number }> {
  if (!navigator.storage?.estimate) return {};
  try {
    const { usage, quota } = await navigator.storage.estimate();
    return { usageBytes: usage, quotaBytes: quota };
  } catch {
    return {};
  }
}
